<template>
    <section class="verification-section">
        <div class="verification-card">
            <div class="logo">
                <img src="@/assets/images/My Oxytocin Trademark.png" alt="My Oxytocin" />
            </div>
            
            <div class="icon-container">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
            </div>
            
            <h1>Verify Your Email</h1>
            
            <p class="message">
                We've sent an 8-digit verification code to:
            </p>
            
            <p class="email">{{ email }}</p>
            
            <p class="instructions">
                Enter the code below to activate your account.
                <span class="timer" v-if="timeRemaining > 0">
                    Code expires in {{ formatTime(timeRemaining) }}
                </span>
                <span class="expired" v-else>
                    Code expired. Please request a new one.
                </span>
            </p>
            
            <!-- Code Input -->
            <div class="code-input-container">
                <input 
                    v-for="(digit, index) in 8" 
                    :key="index"
                    type="text"
                    maxlength="1"
                    class="code-input"
                    :ref="'codeInput' + index"
                    v-model="codeDigits[index]"
                    @input="handleInput(index, $event)"
                    @keydown="handleKeydown(index, $event)"
                    @paste="handlePaste"
                    :disabled="verifying"
                />
            </div>
            
            <div class="actions">
                <button class="btn-primary" @click="verifyCode" :disabled="verifying || !isCodeComplete">
                    <span v-if="verifying">Verifying...</span>
                    <span v-else>Verify Email</span>
                </button>
                
                <button class="btn-secondary" @click="resendCode" :disabled="resending || cooldown > 0">
                    <span v-if="resending">Sending...</span>
                    <span v-else-if="cooldown > 0">Resend in {{ cooldown }}s</span>
                    <span v-else>Resend Code</span>
                </button>
            </div>
            
            <div class="footer-links">
                <router-link to="/login">Back to Login</router-link>
                <span class="divider">|</span>
                <a href="#" @click.prevent="changeEmail">Use Different Email</a>
            </div>
        </div>
    </section>
</template>

<script>
import { auth } from "@/firebase/init.js";
import { signOut } from 'firebase/auth';
import axios from 'axios';

export default {
    name: "EmailVerificationView",
    data() {
        return {
            email: '',
            codeDigits: ['', '', '', '', '', '', '', ''],
            resending: false,
            verifying: false,
            cooldown: 0,
            cooldownInterval: null,
            expiresAt: 0,
            timeRemaining: 0,
            timerInterval: null,
        };
    },
    computed: {
        isCodeComplete() {
            return this.codeDigits.every(d => d !== '');
        },
        fullCode() {
            return this.codeDigits.join('');
        },
        apiBaseUrl() {
            return 'https://us-central1-myoxytocin000-e3049.cloudfunctions.net/api';
        }
    },
    mounted() {
        this.email = this.$route.query.email || (auth.currentUser?.email) || '';
        
        if (!this.email && !auth.currentUser) {
            this.$router.push('/signup');
            return;
        }
        
        // Set initial expiration (15 minutes from now as estimate)
        this.expiresAt = Date.now() + (15 * 60 * 1000);
        this.startExpiryTimer();
    },
    beforeUnmount() {
        if (this.cooldownInterval) clearInterval(this.cooldownInterval);
        if (this.timerInterval) clearInterval(this.timerInterval);
    },
    methods: {
        startExpiryTimer() {
            this.updateTimeRemaining();
            this.timerInterval = setInterval(() => {
                this.updateTimeRemaining();
            }, 1000);
        },
        
        updateTimeRemaining() {
            this.timeRemaining = Math.max(0, this.expiresAt - Date.now());
            if (this.timeRemaining <= 0 && this.timerInterval) {
                clearInterval(this.timerInterval);
            }
        },
        
        formatTime(ms) {
            const minutes = Math.floor(ms / 60000);
            const seconds = Math.floor((ms % 60000) / 1000);
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        },
        
        handleInput(index, event) {
            const value = event.target.value;
            if (value && /^\d$/.test(value)) {
                this.codeDigits[index] = value;
                if (index < 7) {
                    this.$refs['codeInput' + (index + 1)][0].focus();
                }
            } else {
                this.codeDigits[index] = '';
            }
        },
        
        handleKeydown(index, event) {
            if (event.key === 'Backspace' && !this.codeDigits[index] && index > 0) {
                this.$refs['codeInput' + (index - 1)][0].focus();
            }
        },
        
        handlePaste(event) {
            event.preventDefault();
            const pastedData = event.clipboardData.getData('text').replace(/\D/g, '');
            for (let i = 0; i < Math.min(pastedData.length, 8); i++) {
                this.codeDigits[i] = pastedData[i];
            }
        },
        
        async verifyCode() {
            if (!this.isCodeComplete || this.verifying) return;
            
            this.verifying = true;
            
            try {
                const user = auth.currentUser;
                if (!user) {
                    this.$swal({
                        icon: 'error',
                        title: 'Session Expired',
                        text: 'Please sign up again.',
                        confirmButtonColor: '#e37267',
                    });
                    this.$router.push('/signup');
                    return;
                }
                
                const response = await axios.post(`${this.apiBaseUrl}/verify-code`, {
                    uid: user.uid,
                    code: this.fullCode,
                });
                
                if (response.data.success) {
                    this.$swal({
                        icon: 'success',
                        title: 'Email Verified!',
                        text: 'Your account is now active.',
                        confirmButtonColor: '#e37267',
                    }).then(() => {
                        this.$router.push('/');
                    });
                }
            } catch (error) {
                console.error('Error verifying code:', error);
                const errorData = error.response?.data;
                
                let title = 'Verification Failed';
                let text = 'Please try again.';
                
                if (errorData?.error === 'CODE_EXPIRED') {
                    title = 'Code Expired';
                    text = 'Please request a new verification code.';
                } else if (errorData?.error === 'INVALID_CODE') {
                    title = 'Invalid Code';
                    text = errorData.attemptsRemaining !== undefined 
                        ? `Incorrect code. ${errorData.attemptsRemaining} attempts remaining.`
                        : 'Please check the code and try again.';
                } else if (errorData?.error === 'TOO_MANY_ATTEMPTS') {
                    title = 'Too Many Attempts';
                    text = 'Please request a new verification code.';
                }
                
                this.$swal({
                    icon: 'error',
                    title: title,
                    text: text,
                    confirmButtonColor: '#e37267',
                });
                
                // Clear code inputs on error
                this.codeDigits = ['', '', '', '', '', '', '', ''];
                this.$refs['codeInput0'][0].focus();
            } finally {
                this.verifying = false;
            }
        },
        
        async resendCode() {
            if (this.cooldown > 0 || this.resending) return;
            
            this.resending = true;
            
            try {
                const user = auth.currentUser;
                if (!user) {
                    this.$swal({
                        icon: 'error',
                        title: 'Session Expired',
                        text: 'Please sign up again.',
                        confirmButtonColor: '#e37267',
                    });
                    this.$router.push('/signup');
                    return;
                }
                
                const response = await axios.post(`${this.apiBaseUrl}/send-verification-code`, {
                    email: user.email,
                    displayName: user.displayName,
                    uid: user.uid,
                });
                
                if (response.data.success) {
                    this.expiresAt = response.data.expiresAt;
                    this.startExpiryTimer();
                    
                    this.$swal({
                        icon: 'success',
                        title: 'Code Sent!',
                        text: 'A new verification code has been sent to your email.',
                        confirmButtonColor: '#e37267',
                    });
                    
                    // Clear old code
                    this.codeDigits = ['', '', '', '', '', '', '', ''];
                    
                    // Start cooldown
                    this.cooldown = 60;
                    this.cooldownInterval = setInterval(() => {
                        this.cooldown--;
                        if (this.cooldown <= 0) {
                            clearInterval(this.cooldownInterval);
                        }
                    }, 1000);
                }
            } catch (error) {
                console.error('Error resending code:', error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to send verification code. Please try again.',
                    confirmButtonColor: '#e37267',
                });
            } finally {
                this.resending = false;
            }
        },
        
        async changeEmail() {
            try {
                await signOut(auth);
                this.$router.push('/signup');
            } catch (error) {
                console.error('Error signing out:', error);
                this.$router.push('/signup');
            }
        }
    }
};
</script>

<style scoped>
.verification-section {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
    padding: 20px;
}

.verification-card {
    background: white;
    border-radius: 24px;
    padding: 50px 40px;
    max-width: 480px;
    width: 100%;
    text-align: center;
    box-shadow: 0 8px 32px rgba(227, 114, 103, 0.15);
    border: 2px solid #e37267;
}

.logo img {
    height: 50px;
    margin-bottom: 30px;
}

.icon-container {
    width: 80px;
    height: 80px;
    margin: 0 auto 25px;
    background: linear-gradient(135deg, #e37267, #f4a89f);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.icon-container svg {
    width: 40px;
    height: 40px;
    color: white;
}

h1 {
    color: #1b1b1b;
    font-size: 2.4rem;
    font-weight: 700;
    margin: 0 0 20px;
}

.message {
    color: #666;
    font-size: 1.5rem;
    margin: 0 0 10px;
}

.email {
    color: #e37267;
    font-size: 1.6rem;
    font-weight: 600;
    margin: 0 0 20px;
    word-break: break-all;
}

.instructions {
    color: #888;
    font-size: 1.4rem;
    line-height: 1.6;
    margin: 0 0 20px;
}

.timer {
    display: block;
    color: #e37267;
    font-weight: 600;
    margin-top: 8px;
}

.expired {
    display: block;
    color: #dc2626;
    font-weight: 600;
    margin-top: 8px;
}

.code-input-container {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 25px;
}

.code-input {
    width: 42px;
    height: 52px;
    text-align: center;
    font-size: 1.8rem;
    font-weight: 700;
    color: #e37267;
    border: 2px solid #e6d1c7;
    border-radius: 10px;
    background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
    transition: all 0.2s ease;
    font-family: monospace;
}

.code-input:focus {
    outline: none;
    border-color: #e37267;
    box-shadow: 0 0 0 3px rgba(227, 114, 103, 0.2);
}

.code-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 30px;
}

.btn-primary,
.btn-secondary {
    padding: 16px 32px;
    border-radius: 999px;
    font-size: 1.5rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
}

.btn-primary {
    background: linear-gradient(135deg, #e37267, #f4a89f);
    color: white;
    box-shadow: 0 4px 16px rgba(227, 114, 103, 0.3);
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(227, 114, 103, 0.4);
}

.btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-secondary {
    background: white;
    color: #e37267;
    border: 2px solid #e37267;
}

.btn-secondary:hover:not(:disabled) {
    background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
}

.btn-secondary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.footer-links {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 15px;
    font-size: 1.4rem;
}

.footer-links a {
    color: #e37267;
    text-decoration: none;
    font-weight: 500;
}

.footer-links a:hover {
    text-decoration: underline;
}

.footer-links .divider {
    color: #ddd;
}

@media (max-width: 480px) {
    .verification-card {
        padding: 40px 20px;
    }
    
    h1 {
        font-size: 2rem;
    }
    
    .message,
    .email {
        font-size: 1.4rem;
    }
    
    .instructions {
        font-size: 1.3rem;
    }
    
    .code-input-container {
        gap: 5px;
    }
    
    .code-input {
        width: 36px;
        height: 46px;
        font-size: 1.5rem;
        border-radius: 8px;
    }
    
    .btn-primary,
    .btn-secondary {
        font-size: 1.4rem;
        padding: 14px 24px;
    }
}
</style>
